
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { Client } from "@/types/database";

interface CreateBoardDialogProps {
  clients: Client[];
  isLoadingClients: boolean;
  onBoardCreated: () => void;
}

export function CreateBoardDialog({ clients, isLoadingClients, onBoardCreated }: CreateBoardDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const queryClient = useQueryClient();

  const createBoardMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      console.log("Creating board with data:", {
        name,
        description,
        selectedClientId,
        userId: user.id
      });

      // Buscar company_id do usuário
      let targetCompanyId: string | null = null;
      
      // Verificar se é colaborador
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (collaboratorData) {
        targetCompanyId = collaboratorData.company_id;
      } else {
        // Usar própria empresa
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        
        targetCompanyId = userData?.company_id || null;
      }

      console.log("Target company ID:", targetCompanyId);

      // Criar o quadro com visibilidade inicial para o criador
      const { data: board, error } = await supabase
        .from("kanban_boards")
        .insert({
          name,
          description: description || null,
          client_id: selectedClientId,
          user_id: user.id,
          company_id: targetCompanyId,
          visible_to: [user.id]
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating board:", error);
        throw error;
      }

      console.log("Board created successfully:", board);

      // Adicionar automaticamente todos os colaboradores ativos com permissão de workflow
      if (targetCompanyId) {
        const { data: collaborators } = await supabase
          .from('collaborators')
          .select('collaborator_id')
          .eq('company_id', targetCompanyId)
          .eq('status', 'active')
          .contains('permissions', { workflow: true });

        if (collaborators && collaborators.length > 0) {
          const collaboratorIds = collaborators.map(c => c.collaborator_id);
          const allVisibleUsers = [user.id, ...collaboratorIds];
          
          await supabase
            .from('kanban_boards')
            .update({ visible_to: allVisibleUsers })
            .eq('id', board.id);

          console.log("Updated board visibility for collaborators:", allVisibleUsers);
        }
      }

      // Criar colunas padrão
      const defaultColumns = [
        { name: "A Fazer", position: 1 },
        { name: "Em Progresso", position: 2 },
        { name: "Em Revisão", position: 3 },
        { name: "Concluído", position: 4 }
      ];

      const { error: columnsError } = await supabase
        .from("kanban_columns")
        .insert(
          defaultColumns.map(col => ({
            board_id: board.id,
            name: col.name,
            position: col.position
          }))
        );

      if (columnsError) {
        console.error("Error creating columns:", columnsError);
        throw columnsError;
      }

      console.log("Default columns created successfully");

      return board;
    },
    onSuccess: () => {
      toast.success("Quadro criado com sucesso!");
      setOpen(false);
      setName("");
      setDescription("");
      setSelectedClientId("");
      onBoardCreated();
    },
    onError: (error) => {
      console.error("Error creating board:", error);
      toast.error("Erro ao criar quadro");
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !selectedClientId) {
      toast.error("Nome do quadro e cliente são obrigatórios");
      return;
    }
    createBoardMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Novo Quadro
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Criar Novo Quadro</DialogTitle>
          <DialogDescription>
            Crie um novo quadro de tarefas para organizar seu fluxo de trabalho.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome do Quadro</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Projeto Website"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="client">Cliente</Label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId} required>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um cliente" />
              </SelectTrigger>
              <SelectContent>
                {isLoadingClients ? (
                  <SelectItem value="loading" disabled>Carregando...</SelectItem>
                ) : clients.length === 0 ? (
                  <SelectItem value="no-clients" disabled>Nenhum cliente encontrado</SelectItem>
                ) : (
                  clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição (opcional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o objetivo deste quadro..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setOpen(false)}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={createBoardMutation.isPending || !name.trim() || !selectedClientId}
            >
              {createBoardMutation.isPending ? "Criando..." : "Criar Quadro"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
